import React from 'react';
import { Box, IconButton, Tooltip } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { BRAND } from '../theme/tokens';

/** Narrow left-rail width when the preview pane is collapsed (FI parity — PO, Bill, Quote). */
export const COLLAPSE_THUMB_WIDTH = 48;

/**
 * Lean expand/collapse divider — used between the left preview panel and the
 * main reconciliation panel on Statement Recon, Bill Detail, and Quote→PO
 * dialog. Renders a slim 14px column with a vertical hairline and a floating
 * 18px circular chevron button at the top.
 *
 * Pattern:
 * ┌─┐
 * │·│ ← chevron button (top: 48)
 * │ │
 * │ │ ← 1px hairline
 * │ │
 *
 * Consumers wrap their preview pane in a `<DocumentPreviewRail>` (or a Box
 * width={collapsed ? THUMB : N}) and place this divider immediately after it.
 * Optional drag-to-resize is
 * left to the consumer (this component only handles the collapse toggle).
 */
export interface CollapseDividerProps {
  collapsed: boolean;
  onToggle: () => void;
  /** Optional: called on mousedown when NOT collapsed — for drag-to-resize. */
  onDragStart?: (e: React.MouseEvent) => void;
  /** Override tooltip text for the chevron button. */
  expandLabel?: string;
  collapseLabel?: string;
  /** Vertical offset of the chevron from the top of the divider. */
  topOffset?: number;
}

export const CollapseDivider: React.FC<CollapseDividerProps> = ({
  collapsed,
  onToggle,
  onDragStart,
  expandLabel = 'Expand preview',
  collapseLabel = 'Collapse preview',
  topOffset = 48,
}) => {
  const canDrag = !collapsed && !!onDragStart;
  return (
    <Box
      className="no-print"
      sx={{
        width: canDrag ? 10 : 14, flexShrink: 0, alignSelf: 'stretch',
        position: 'relative',
        display: 'flex', justifyContent: 'center',
        cursor: collapsed ? 'default' : (onDragStart ? 'col-resize' : 'default'),
        mx: 0.25,
        '&:hover .collapse-divider-line': canDrag ? { backgroundColor: BRAND, width: 3 } : undefined,
      }}
      onMouseDown={(e) => {
        if (!collapsed && onDragStart) onDragStart(e);
      }}
      title={canDrag ? 'Drag to resize preview' : undefined}
    >
      {/* Hairline rail — thicker hit target when drag-resize is armed */}
      <Box
        className="collapse-divider-line"
        sx={{
          width: canDrag ? 2 : 1,
          backgroundColor: canDrag ? '#c5bdd8' : '#e0e0e0',
          height: '100%',
          borderRadius: 1,
          transition: 'background-color 0.12s, width 0.12s',
        }}
      />
      {/* Floating chevron button */}
      <Tooltip title={collapsed ? expandLabel : collapseLabel} arrow placement="right">
        <IconButton
          size="small"
          onClick={onToggle}
          onMouseDown={(e) => e.stopPropagation()}
          sx={{
            position: 'absolute', top: topOffset,
            width: 18, height: 18, p: 0,
            backgroundColor: '#fff',
            border: '1px solid #d0d0d0', borderRadius: '50%',
            '&:hover': { backgroundColor: '#f5f0ff', borderColor: BRAND, color: BRAND },
            zIndex: 2,
          }}
        >
          {collapsed
            ? <ChevronRightIcon sx={{ fontSize: 12 }} />
            : <ChevronLeftIcon  sx={{ fontSize: 12 }} />}
        </IconButton>
      </Tooltip>
    </Box>
  );
};
